import { Link } from 'react-router-dom';
import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { carName, carTagline } from '../../domain/cars/carCatalogue';
import { catalogueTake, hasOwnGround } from '../../domain/cars/carSchema';
import { artSource } from './carArt';

/**
 * One car on the garage shelf (FR-03).
 *
 * THE ARTWORK IS THE DESIGN. Everything else on this card is arranged around
 * getting out of its way: no glyph row, no meter, no save control, no gradient
 * scrim laid across the picture. A 2000px cut-out of a Countach does not need
 * help, and the sibling product's own history here is the argument — it printed
 * a five-column plate of icons on a 163px tile and had to take it all back off.
 *
 * The card is BIG and the grid is one column on a phone, because this is a
 * gallery rather than a table. Twelve cars at a generous height is a scroll
 * somebody enjoys; twelve cars at list-row height is a spreadsheet with pictures
 * in it.
 *
 * TWO TREATMENTS, and the schema decides which:
 *
 *   GROUNDED  the car has a front view, which arrives with its own lit ground.
 *             It goes full bleed and the plate is dropped entirely — a livery
 *             wash under a picture that already has one would be a second colour
 *             fighting the first.
 *   PLATED    the car has only its side cut-out, on transparency. It sits on a
 *             plate tinted by its OWN paint (`--livery-*`), so a yellow RX-7 and
 *             a red F40 do not share a ground and the artwork stays the reason
 *             to look. The component never authors a colour: it names a token
 *             and lets the sheet resolve it, which is what keeps `tokens.css`
 *             the only file with a hex in it.
 *
 * The whole card is ONE link. There is no second control on it, so there is
 * nothing that has to sit outside the anchor — which is what lets the tap target
 * be the card rather than the words on it.
 */
export default function CarCard({ car, search = '', isFirst = false }) {
    const { localeCode } = useLocale();
    const t = useTranslate();
    const take = catalogueTake(car);
    const art = artSource(car, take);
    const name = carName(car);
    const tagline = carTagline(car);

    return (
        <article
            className={`car-card${hasOwnGround(take) ? ' car-card--grounded' : ''}`}
            style={{ '--livery': `var(--livery-${car.livery})` }}
        >
            {/* The shelf's query rides along. It is what makes the deck on the
                other side the shelf the reader was just looking at rather than
                the whole catalogue — `useCarDeck` reads the filters back out of
                the address bar, and a link that dropped them would deal a car
                the garage had filtered out. */}
            <Link className="car-card__link" to={{ pathname: `/${localeCode}/cars/${car.id}`, search }}>
                <span className="car-card__stage">
                    {art ? (
                        <img
                            className="car-card__art"
                            src={art.src}
                            srcSet={art.srcSet}
                            /* The shelf's column count, restated for a parser
                               that runs BEFORE the stylesheet.
                               ------------------------------------------------
                               This is the mechanism the whole high-resolution
                               story hangs on, and it fails silently in the one
                               direction nobody checks: `sizes` is read at
                               preload time, so a card widened to 400px while
                               this still said 300px would fetch the 480 rung and
                               upscale it — a wide screen that looks WORSE than
                               the phone, with no error anywhere.

                               Each step mirrors `.garage__grid` in `garage.css`
                               against `--stage-max` in `layout.css`: two columns
                               inside the 640px cap, three from 1024 in a 1120px
                               stage, four from 1440. Change either sheet and
                               this line is part of the change. */
                            sizes="(min-width: 1920px) 405px, (min-width: 1440px) 335px, (min-width: 1024px) 360px, (min-width: 640px) 300px, 92vw"
                            /* The picture repeats what the heading beside it
                               already says, so announcing it again would only
                               slow a screen reader down through twelve cards. */
                            alt=""
                            /* The first card is above the fold on every phone in
                               the matrix and is the largest thing on the screen —
                               it is this product's Largest Contentful Paint, so
                               it is fetched eagerly and at high priority while
                               the eleven below it wait until they are near. */
                            loading={isFirst ? 'eager' : 'lazy'}
                            fetchPriority={isFirst ? 'high' : undefined}
                            decoding="async"
                        />
                    ) : null}
                    <span className={`rarity-ribbon rarity-ribbon--${car.rarity} car-card__rarity`}>
                        {t(`cars.rarity.${car.rarity}`)}
                    </span>
                </span>

                <span className="car-card__hairline heat-hairline" aria-hidden="true" />

                <span className="car-card__head">
                    {/* A heading, so the garage is a navigable list of twelve
                        things rather than twelve anonymous links. */}
                    <h2 className="car-card__name">{name}</h2>
                    <span className="car-card__year figure">{car.year}</span>
                </span>

                {/* The one line of prose a card gets. It is the car's own
                    tagline, not a truncated description: a clamped paragraph
                    ends mid-sentence on some phone somewhere, and this is
                    authored to be one line everywhere. */}
                {tagline ? <span className="car-card__tagline">{tagline}</span> : null}
            </Link>
        </article>
    );
}
